import React from 'react';
import { Button } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import { ProductOrderItem } from '../../../types/products';
import { useCurrency } from '../../../hooks/useCurrency';

interface OrderCartPanelProps {
  items: ProductOrderItem[];
  /** Shown in the header: "Orden · María" — confirms who you're capturing for */
  customerLabel?: string;
  submitLabel: string;
  submitting: boolean;
  onSubmit: () => void;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onRemoveItem: (index: number) => void;
  /** Tapping the row opens variant/notes editing (mirrors tapping a product tile) */
  onEditItem: (index: number) => void;
}

/**
 * Compact cart: 2 lines per item (stepper · name+meta · total · ✕).
 * Replaces the 5-row inline summaries duplicated in TakeOut and Delivery.
 */
const OrderCartPanel: React.FC<OrderCartPanelProps> = ({
  items,
  customerLabel,
  submitLabel,
  submitting,
  onSubmit,
  onUpdateQuantity,
  onRemoveItem,
  onEditItem,
}) => {
  const { currencySymbol } = useCurrency();
  const money = (n: number) =>
    `${currencySymbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

  const metaFor = (item: ProductOrderItem): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    if (item.dailyMenuSelections?.length) {
      parts.push(item.dailyMenuSelections.map(s => s.optionName).filter(Boolean).join(' · '));
    } else if (item.variantSelections?.length) {
      parts.push(item.variantSelections.map(v => (v.quantity ? `${v.quantity} ${v.name}` : v.name)).join(', '));
    } else if (item.selectedVariants.length) {
      parts.push(item.selectedVariants.map(v => v.name).join(', '));
    }
    if (item.notes) {
      parts.push(<span key="note" className="ot-note">{item.notes}</span>);
    }
    if (parts.length === 0) return ' ';
    return parts.map((p, i) => <React.Fragment key={i}>{i > 0 && ' · '}{p}</React.Fragment>);
  };

  return (
    <div className="ot-panel ot-cart">
      <div className="ot-cart__head">
        <span className="ot-cart__title">
          Orden{customerLabel ? ` · ${customerLabel}` : ' · nueva'}
        </span>
        <span className="ot-cart__count">
          {totalItems} {totalItems === 1 ? 'item' : 'items'}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="ot-cart__empty">
          <ShoppingOutlined style={{ fontSize: 34, color: 'var(--border-color-strong)', display: 'block', marginBottom: 8 }} />
          Agrega productos a la orden
        </div>
      ) : (
        <div className="ot-cart__items">
          {items.map((item, index) => (
            <div
              key={index}
              className="ot-item"
              onClick={() => onEditItem(index)}
            >
              {item.dailyMenuId ? (
                // Daily-menu items don't have a free quantity; edit via tap
                <span className="ot-item__stepper" onClick={(e) => e.stopPropagation()}>
                  <b style={{ padding: '0 8px' }}>{item.quantity}×</b>
                </span>
              ) : (
                <span className="ot-item__stepper" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                    aria-label="Quitar uno"
                  >
                    −
                  </button>
                  <b>{item.quantity}</b>
                  <button
                    onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                    disabled={item.quantity >= 99}
                    aria-label="Agregar uno"
                  >
                    +
                  </button>
                </span>
              )}

              <span className="ot-item__info">
                <div className="ot-item__name">{item.productName}</div>
                <div className="ot-item__meta">{metaFor(item)}</div>
              </span>

              <span className="ot-item__price">{money(item.quantity * item.unitPrice)}</span>

              <button
                className="ot-item__remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveItem(index);
                }}
                aria-label={`Eliminar ${item.productName}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="ot-cart__foot">
        <div className="ot-cart__total">
          <span className="ot-cart__total-label">
            Total{totalItems > 0 ? ` · ${totalItems} producto${totalItems === 1 ? '' : 's'}` : ''}
          </span>
          <span className="ot-cart__total-amount">{money(totalAmount)}</span>
        </div>
        <Button
          type="primary"
          size="large"
          block
          onClick={onSubmit}
          loading={submitting}
          disabled={items.length === 0}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

export default OrderCartPanel;
